import type { SessionView } from "src/network/authService";
import type { Organisation } from "src/types/commonTypes";
import { isCookieAuthMode } from "src/config/Config";

export type SessionClaims = {
  organisations: Organisation[];
  currentOrganisation?: Organisation;
  subscriptionIds: string[];
  defaultOrgId?: string;
  currentRole?: string;
};

type AuthSessionState = {
  session: SessionView | null;
  cognitoAccessToken: string | null;
};

export const hasActiveSession = (auth: AuthSessionState): boolean =>
  isCookieAuthMode() ? Boolean(auth.session) : Boolean(auth.cognitoAccessToken);

// The BFF blocks GraphQL until a factor is enrolled; the web only shows the setup screen while this is set.
export const isMfaEnrolmentRequired = (session: SessionView | null | undefined): boolean =>
  isCookieAuthMode() && session?.mfa_enrolment_required === true;

export const getClaimsFromSession = (session: SessionView): SessionClaims => {
  const organisations = Array.isArray(session.available_relations) ? session.available_relations : [];
  const currentOrganisation = organisations.find((org) => org.organisation_id === session.current_organisation);

  return {
    organisations,
    currentOrganisation,
    subscriptionIds: Array.isArray(session.subscription_ids) ? session.subscription_ids : [],
    defaultOrgId: session.default_organisation ?? undefined,
    currentRole: session.current_role ?? undefined
  };
};

const SESSION_EXPIRED_KEY = "session_expired";

// sessionStorage survives logout's localStorage.clear(), so the sign-in page can say why the user landed there.
export const markSessionExpired = () => {
  if (isCookieAuthMode()) sessionStorage.setItem(SESSION_EXPIRED_KEY, "true");
};

export const consumeSessionExpired = (): boolean => {
  if (!isCookieAuthMode() || !sessionStorage.getItem(SESSION_EXPIRED_KEY)) return false;
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  return true;
};

const LEGACY_STORAGE_CLEANUP_KEY = "spendable_auth_storage";
const LEGACY_STORAGE_CLEANUP_VALUE = "cookie-v1";
const LEGACY_TOKEN_KEY_PREFIXES = ["CognitoIdentityServiceProvider.", "amplify-"];
const LEGACY_TOKEN_FIELDS = ["cognitoIdToken", "cognitoAccessToken", "cognitoRefreshToken", "token"];
const PERSIST_ROOT_KEY = "persist:root";

const stripPersistedTokens = () => {
  const raw = localStorage.getItem(PERSIST_ROOT_KEY);

  if (!raw) return;
  try {
    const root = JSON.parse(raw) as Record<string, string>;

    if (typeof root.auth !== "string") return;
    const auth = JSON.parse(root.auth) as Record<string, unknown>;

    LEGACY_TOKEN_FIELDS.forEach((field) => {
      auth[field] = null;
    });
    root.auth = JSON.stringify(auth);
    localStorage.setItem(PERSIST_ROOT_KEY, JSON.stringify(root));
  } catch {
    localStorage.removeItem(PERSIST_ROOT_KEY);
  }
};

// Runs once per browser when cookie mode is first loaded: Amplify token caches and persisted token fields go away.
export const purgeLegacyTokenStorage = () => {
  if (!isCookieAuthMode()) return;
  if (localStorage.getItem(LEGACY_STORAGE_CLEANUP_KEY) === LEGACY_STORAGE_CLEANUP_VALUE) return;

  Object.keys(localStorage)
    .filter((key) => LEGACY_TOKEN_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)))
    .forEach((key) => localStorage.removeItem(key));
  stripPersistedTokens();
  localStorage.setItem(LEGACY_STORAGE_CLEANUP_KEY, LEGACY_STORAGE_CLEANUP_VALUE);
};
